import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import dayjs from 'dayjs';
import { messageShow } from '../../common/api/actions';
import EmptyMessage from '../../common/EmptyMessage';
import Image from '../../common/Image';
import BrandedLoading from '../../common/BrandedLoading';
import Pagination from '../../common/Pagination';
import SectionPaper from '../../common/SectionPaper';
import { listCustomers } from '../../user/api/actions/query';
import { deleteUser } from '../../user/api/actions/mutation';
import { getImageSource } from '../../user/api/routes';
import routes from '../../../setup/routes';

const styles = theme => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
  },
  searchFieldsAll: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  tableContainer: {
    overflowX: 'auto',
  },
});

class CustomerUnsubscribed extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      customers: [],
      count: 0,
      page: 1,
      limit: 10,
      searchField: 'all',
      searchQuery: '',
      sortField: 'createdAt',
      sortOrder: 'desc',
    };
  }

  componentDidMount() {
    this.fetchCustomers(1);
  }

  fetchCustomers = async (page = 1) => {
    const { listCustomers, messageShow } = this.props;
    const { limit, searchField, searchQuery, sortField, sortOrder } = this.state;

    this.setState({ isLoading: true });

    try {
      const filters = {
        emailSubscription: false,
      };

      if (searchField && searchField !== 'all' && searchQuery.trim()) {
        filters[searchField] = searchQuery.trim();
      }

      const { data } = await listCustomers({
        page,
        limit,
        filter: filters,
        sortField,
        sortOrder,
      });

      if (data.success && data.data) {
        const customerList = Array.isArray(data.data.list)
          ? data.data.list
          : Array.isArray(data.data)
          ? data.data
          : [];
        const totalCount = typeof data.data.count === 'number'
          ? data.data.count
          : (data.count !== undefined ? data.count : customerList.length);

        this.setState({
          customers: customerList,
          count: totalCount,
          page,
        });
      } else {
        messageShow(data.message || 'Failed to fetch unsubscribed customers.');
      }
    } catch (error) {
      messageShow('There was some error fetching unsubscribed customers.');
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ page }, () => this.fetchCustomers(page));
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => this.fetchCustomers(1));
  };

  handleClearSearch = () => {
    this.setState({ searchQuery: '', searchField: 'all', page: 1 }, () => {
      this.fetchCustomers(1);
    });
  };

  handleSort = (field) => {
    this.setState(
      (prevState) => ({
        sortField: field,
        sortOrder: prevState.sortField === field && prevState.sortOrder === 'asc' ? 'desc' : 'asc',
      }),
      () => this.fetchCustomers(this.state.page)
    );
  };

  onDelete = async (userId) => {
    if (window.confirm('Are you sure you want to soft delete this customer? Their record will be safely marked as deleted.')) {
      const { deleteUser, messageShow } = this.props;
      try {
        const { data } = await deleteUser({ userId });
        messageShow(data.message || 'Customer deleted successfully.');
        if (data.success) {
          this.fetchCustomers(this.state.page);
        }
      } catch (error) {
        messageShow('Some error occurred while deleting customer.');
      }
    }
  };

  exportCSV = () => {
    const { customers } = this.state;
    const customerList = Array.isArray(customers) ? customers : (customers?.list || []);
    if (!customerList || customerList.length === 0) return;

    const headers = ['Name', 'Email', 'Mobile', 'Pincode', 'Joined Date', 'Email Status'];
    const rows = customerList.map(c => [
      `"${c.name || ''}"`,
      `"${c.email || ''}"`,
      `"${c.mobile || ''}"`,
      `"${c.pincode || ''}"`,
      `"${c.createdAt ? dayjs(c.createdAt).format('YYYY-MM-DD HH:mm') : ''}"`,
      `"Unsubscribed"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `customer_email_unsubscribed_list_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { classes, history } = this.props;
    const {
      customers,
      isLoading,
      count,
      page,
      limit,
      searchField,
      searchQuery,
      sortField,
      sortOrder,
    } = this.state;

    const customerList = Array.isArray(customers) ? customers : (customers && Array.isArray(customers.list) ? customers.list : []);

    return (
      <div style={{ padding: '20px' }}>
        {/* Top Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(routes.customerList.path(1))}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#cbd5e1',
                color: '#475569',
                fontWeight: 600,
                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
              }}
            >
              Back to Customers
            </Button>
            <Typography variant="h5" style={{ fontWeight: 800, color: '#0d3559' }}>
              Email Unsubscribed List — Customers
            </Typography>
            <Chip
              size="small"
              label={`${count} Unsubscribed`}
              sx={{
                bgcolor: '#fee2e2',
                color: '#dc2626',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={this.exportCSV}
            disabled={customerList.length === 0}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Export Unsubscribed CSV
          </Button>
        </Box>

        {/* Regulatory & Compliance Notice */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#fef2f2',
            border: '1px solid #fee2e2',
            borderRadius: '12px',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <UnsubscribeIcon sx={{ color: '#ef4444', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>
              Email Unsubscribed List & Registry
            </Typography>
            <Typography variant="body2" sx={{ color: '#b91c1c', fontSize: '0.85rem' }}>
              In accordance with CAN-SPAM, GDPR, and CASL regulations, customers listed here have explicitly opted out of marketing communications. Marketing emails will not be delivered to these addresses.
            </Typography>
          </Box>
        </Box>

        {/* Search Toolbar */}
        <Toolbar className={classes.toolbar} style={{ padding: 0, marginBottom: '16px' }}>
          <div className={classes.searchFieldsAll}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                  <InputLabel id="search-field-label">Search By</InputLabel>
                  <Select
                    labelId="search-field-label"
                    value={searchField}
                    onChange={(e) => this.setState({ searchField: e.target.value })}
                    label="Search By"
                    style={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="all">All Fields</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="pincode">Pincode</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search unsubscribed customer..."
                  value={searchQuery}
                  onChange={(e) => this.setState({ searchQuery: e.target.value })}
                  style={{ minWidth: 260 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={this.handleSearch}
                  sx={{
                    bgcolor: '#0d3559',
                    color: 'white',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#092a46', boxShadow: 'none' },
                  }}
                >
                  Search
                </Button>
              </Grid>
              {searchQuery && (
                <Grid item>
                  <Button
                    variant="text"
                    onClick={this.handleClearSearch}
                    sx={{ textTransform: 'none', color: '#64748b' }}
                  >
                    Clear Filter
                  </Button>
                </Grid>
              )}
            </Grid>
          </div>
        </Toolbar>

        {/* Table View */}
        <SectionPaper>
          {isLoading ? (
            <BrandedLoading />
          ) : customerList.length === 0 ? (
            <EmptyMessage message="No unsubscribed customers found." />
          ) : (
            <>
              <div className={classes.tableContainer}>
                <Table padding="normal">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell width={75}>Image</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => this.handleSort('name')}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#334155' }}
                        >
                          Name
                          {sortField === 'name' ? (sortOrder === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />) : null}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => this.handleSort('email')}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#334155' }}
                        >
                          Email ID
                          {sortField === 'email' ? (sortOrder === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />) : null}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => this.handleSort('mobile')}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#334155' }}
                        >
                          Mobile
                          {sortField === 'mobile' ? (sortOrder === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />) : null}
                        </Button>
                      </TableCell>
                      <TableCell>Pincode</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => this.handleSort('createdAt')}
                          sx={{ textTransform: 'none', fontWeight: 700, color: '#334155' }}
                        >
                          Joined
                          {sortField === 'createdAt' ? (sortOrder === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />) : null}
                        </Button>
                      </TableCell>
                      <TableCell align="center">Email Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerList.map((customer) => (
                      <TableRow key={customer._id} hover>
                        <TableCell>
                          <Link to={routes.customerDetail.path(customer._id)}>
                            <Image src={getImageSource(customer.image)} defaultSrc={getImageSource()} />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={routes.customerDetail.path(customer._id)}
                            style={{ textDecoration: 'none', color: '#0d3559', fontWeight: 600 }}
                          >
                            {customer.name || 'Unnamed'}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{customer.email}</span>
                          </Box>
                        </TableCell>
                        <TableCell>{customer.mobile || '-'}</TableCell>
                        <TableCell>{customer.pincode || '-'}</TableCell>
                        <TableCell>
                          {customer.createdAt ? dayjs(customer.createdAt).format('DD MMM YYYY') : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label="UNSUBSCRIBED"
                            sx={{
                              bgcolor: '#fee2e2',
                              color: '#dc2626',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              letterSpacing: '0.5px',
                              borderRadius: '6px',
                              height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Soft Delete Customer">
                            <IconButton
                              size="small"
                              onClick={() => this.onDelete(customer._id)}
                              sx={{
                                color: '#ef4444',
                                '&:hover': { bgcolor: '#fee2e2' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                  count={count}
                  page={page}
                  limit={limit}
                  onChange={this.handlePageChange}
                />
              </Box>
            </>
          )}
        </SectionPaper>
      </div>
    );
  }
}

CustomerUnsubscribed.propTypes = {
  classes: PropTypes.object.isRequired,
  listCustomers: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
};

export default withRouter(
  connect(null, { listCustomers, deleteUser, messageShow })(
    withStyles(styles)(CustomerUnsubscribed)
  )
);